import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Form, FormikProvider, useFormik } from 'formik';
// @mui
import { Stack, IconButton, InputAdornment, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '../../../components/iconify';
import { PASSWORD_REGEX } from '../../../Constants/RegexConstants';
import { changePassword } from '../../../services/changePassword';

// ----------------------------------------------------------------------

export default function ChangePasswordForm() {
    const navigate = useNavigate();

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const ChangePasswordSchema = Yup.object().shape({
        currentPassword: Yup.string()
            .required('Current Password is required')
            .matches(
                PASSWORD_REGEX,
                'Password should contain at least one number and one special character having length between 6 To 16 characters.'
            ),
        newPassword: Yup.string()
            .required('New Password is required')
            .matches(
                PASSWORD_REGEX,
                'Password should contain at least one number and one special character having length between 6 To 16 characters.'
            )
            .notOneOf(
                [Yup.ref('currentPassword')],
                'The current Password and New Password should not be the same. Please enter a different password.'
              ),
        confirmPassword: Yup.string()
            .required('Confirm password is required')
            .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
    });

    const formik = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: ChangePasswordSchema,

        onSubmit: (values) => {
            const body = {...values};
            delete body.confirmPassword;
            changePassword(body)
                .then(({ data }) => {
                    if (data?.status === "success") {
                        navigate('/dashboard');
                        toast.success("Password updated");
                    }
                })
                .catch((error) => {
                    setSubmitting(false);
                    toast.error(error?.response?.data?.message)
                })
                .finally(() => {
                    setSubmitting(false);
                });
        },
    });

    const {
        errors,
        touched,
        handleSubmit,
        getFieldProps,
        isSubmitting,
        setSubmitting,
    } = formik;

    return (
        <>
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <TextField
                            name="currentPassword"
                            label="Current Password"
                            {...getFieldProps('currentPassword')}
                            error={touched.currentPassword && Boolean(errors.currentPassword)}
                            helperText={touched.currentPassword && errors.currentPassword}
                            type={showCurrentPassword ? 'text' : 'password'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                                            <Iconify icon={showCurrentPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            name="newPassword"
                            label="New Password"
                            {...getFieldProps('newPassword')}
                            error={touched.newPassword && Boolean(errors.newPassword)}
                            helperText={touched.newPassword && errors.newPassword}
                            type={showNewPassword ? 'text' : 'password'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                            <Iconify icon={showNewPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            name="confirmPassword"
                            label="Confirm Password"
                            style={{ marginBottom: '24px' }}
                            {...getFieldProps('confirmPassword')}
                            error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                            helperText={touched.confirmPassword && errors.confirmPassword}
                            type={showConfirmPassword ? 'text' : 'password'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                            <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>

                    <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
                        Change Password
                    </LoadingButton>
                </Form>
            </FormikProvider>
        </>
    );
}
